import { useEffect, useRef, useState } from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { facilitiesApi } from "./api";
import { useFacilityRole } from "./useFacilityRole";
import { tasksApi } from "@/features/tasks/api";

// Routes that live behind the mobile "More" pill. The substring match is
// fine because nothing else in the app collides on these suffixes.
const OVERFLOW_PATHS = ["inspections", "members", "settings"] as const;

export function FacilityLayout() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const facilityId = Number(id);

  const facilityQ = useQuery({
    queryKey: ["facility", facilityId],
    queryFn: () => facilitiesApi.get(facilityId),
    enabled: !Number.isNaN(facilityId),
  });

  // Lightweight badge counts for the tab nav.
  const itemsQ = useQuery({
    queryKey: ["items", facilityId],
    queryFn: () => facilitiesApi.listItems(facilityId),
    enabled: !Number.isNaN(facilityId),
  });
  const proposedQ = useQuery({
    queryKey: ["tasks", facilityId, "proposed"],
    queryFn: () => tasksApi.list(facilityId, "proposed"),
    enabled: !Number.isNaN(facilityId),
  });
  const activeTasksQ = useQuery({
    queryKey: ["tasks", facilityId, "active"],
    queryFn: () => tasksApi.list(facilityId, "active"),
    enabled: !Number.isNaN(facilityId),
  });

  if (Number.isNaN(facilityId)) {
    return <div className="p-6 text-slate-500">Bad facility ID.</div>;
  }

  const facility = facilityQ.data;
  const itemCount = itemsQ.data?.count ?? null;
  const activeTaskCount = activeTasksQ.data?.count ?? null;
  // Proposed tasks are an owner-only review queue — never surface the
  // badge to maintainers.
  const { isOwner } = useFacilityRole(facilityId);
  const proposedCount = isOwner ? (proposedQ.data?.count ?? null) : null;

  return (
    <div>
      <header className="border-b border-slate-200">
        <div className="pb-3">
          <p className="text-xs uppercase tracking-wide text-slate-500">Facility</p>
          <h1 className="mt-1 truncate text-2xl font-semibold tracking-tight">
            {facility?.name ?? "…"}
          </h1>
          {facility && (
            <a
              href={`/f/${facility.public_token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t("facility.publicLink", "Open public report page")}
              <ExternalLinkIcon />
            </a>
          )}
        </div>
        {/* The scrolling tabs row is wrapped in a flex container so the
            More popover can render *outside* the overflow-x-auto strip
            — otherwise the absolute-positioned popover gets clipped by
            the scroll container and disappears behind the page body. */}
        <nav className="-mb-px flex items-end justify-between text-sm">
          <div className="flex flex-1 gap-6 overflow-x-auto">
            <Tab to="" end>{t("facility.tabs.overview")}</Tab>
            <Tab to="items">{t("facility.tabs.items")} {badge(itemCount)}</Tab>
            <Tab to="reports">{t("facility.tabs.reports")}</Tab>
            <Tab to="tasks">
              {t("facility.tabs.tasks")}{" "}
              {badge(activeTaskCount)}
              {proposedCount ? (
                <span className="ml-1 rounded-full bg-brand-50 px-1.5 text-xs font-medium text-brand-600">
                  +{proposedCount}
                </span>
              ) : null}
            </Tab>
            {/* Overflow tabs inline on ≥ sm; folded into More ▾ below sm. */}
            <div className="hidden gap-6 sm:contents">
              <OverflowTabs t={t} isOwner={isOwner} />
            </div>
          </div>
          <MoreMenu isOwner={isOwner} />
        </nav>
      </header>
      <div className="pt-6">
        <Outlet />
      </div>
    </div>
  );
}

function OverflowTabs({
  t,
  isOwner,
}: {
  t: (k: string) => string;
  isOwner: boolean;
}) {
  return (
    <>
      <span className="border-b-2 border-transparent pb-3 text-slate-400">
        {t("facility.tabs.inspections")}{" "}
        <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] font-medium uppercase text-slate-500">
          {t("facility.soon")}
        </span>
      </span>
      <Tab to="members">{t("facility.tabs.members")}</Tab>
      {isOwner && <Tab to="settings">{t("facility.tabs.settings")}</Tab>}
    </>
  );
}

/** Mobile-only "More ▾" pill. Anchors a popover with the overflow tabs. */
function MoreMenu({ isOwner }: { isOwner: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const activeOverflow = OVERFLOW_PATHS.some((p) =>
    location.pathname.endsWith(`/${p}`),
  );

  // Close on outside click + Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative sm:hidden" ref={wrapperRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="facility-tabs-more"
        onClick={() => setOpen((v) => !v)}
        className={
          "border-b-2 pb-3 " +
          (activeOverflow
            ? "border-brand-500 font-medium text-brand-600"
            : "border-transparent text-slate-600 hover:text-slate-900")
        }
      >
        {t("facility.tabs.more")} ▾
      </button>
      {open && (
        <div
          id="facility-tabs-more"
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 w-48 rounded-md border border-slate-200 bg-white py-1 shadow-md"
        >
          <span
            role="menuitem"
            aria-disabled
            className="flex items-center justify-between px-3 py-2 text-sm text-slate-400"
          >
            {t("facility.tabs.inspections")}
            <span className="rounded bg-slate-100 px-1 text-[10px] font-medium uppercase text-slate-500">
              {t("facility.soon")}
            </span>
          </span>
          <MenuLink to="members" onNavigate={() => setOpen(false)}>
            {t("facility.tabs.members")}
          </MenuLink>
          {isOwner && (
            <MenuLink to="settings" onNavigate={() => setOpen(false)}>
              {t("facility.tabs.settings")}
            </MenuLink>
          )}
        </div>
      )}
    </div>
  );
}

function MenuLink({
  to,
  onNavigate,
  children,
}: {
  to: string;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      onClick={onNavigate}
      className={({ isActive }) =>
        "block px-3 py-2 text-sm " +
        (isActive
          ? "bg-brand-50 font-medium text-brand-700"
          : "text-slate-700 hover:bg-slate-50")
      }
    >
      {children}
    </NavLink>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d="M14 4h6v6" />
      <path d="M10 14L20 4" />
      <path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

function badge(count: number | null) {
  if (count == null) return null;
  return <span className="ml-1 text-xs text-slate-400">{count}</span>;
}

function Tab({
  to,
  end,
  children,
}: {
  to: string;
  end?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        "border-b-2 pb-3 " +
        (isActive
          ? "border-brand-500 font-medium text-brand-600"
          : "border-transparent text-slate-600 hover:text-slate-900")
      }
    >
      {children}
    </NavLink>
  );
}
